'use client'

import { useEffect, useState } from 'react'
import { cn } from '@/components/ui'
import { quandSeance } from '@/lib/compte-a-rebours'

/**
 * Le surtitre et la ligne en grand d'un ticket de séance — la tête du ticket,
 * hors repères et intitulé.
 *
 * Sans hook : `Ticket` (composant serveur) s'en sert tel quel quand la séance
 * n'a pas de compte à rebours, `CompteARebours` quand elle en a un. Un seul
 * dessin pour les deux.
 */
export function SurtitreEtJour({ surtitre, jour }: { surtitre?: string; jour: string }) {
  return (
    <>
      {surtitre && <span className="u-legende block text-ink">{surtitre}</span>}
      <p
        className={cn(
          'mt-2 font-black tracking-[-0.05em] leading-[0.9] text-ink',
          'text-[42px] oasis:text-[clamp(44px,3.8vw,88px)]',
        )}
      >
        {jour}
      </p>
    </>
  )
}

/**
 * La même tête, qui compte le temps jusqu'à la séance (voir
 * `lib/compte-a-rebours.ts` pour ce qu'elle dit et quand).
 *
 * ⚠️ **Le premier rendu se fait à l'instant du serveur**, passé en
 * `maintenant` : le navigateur rend d'abord exactement le texte reçu, sans
 * désaccord d'hydratation, puis prend sa propre horloge au premier battement.
 * Partir de `Date.now()` dès le rendu donnerait un texte différent dès que la
 * page arrive à cheval sur une minute.
 *
 * ⚠️ Pas de région `aria-live` : un lecteur d'écran annoncerait le décompte à
 * chaque minute. Le texte reste lisible à la demande, comme le reste du ticket.
 */
export function CompteARebours({
  debut,
  fin,
  maintenant,
  jour,
  surtitre,
}: {
  /** Instants en millisecondes : ils traversent la frontière serveur → client. */
  debut: number
  fin: number
  maintenant: number
  jour: string
  surtitre?: string
}) {
  const [instant, setInstant] = useState(maintenant)

  useEffect(() => {
    const battement = setInterval(() => setInstant(Date.now()), 1000)
    return () => clearInterval(battement)
  }, [])

  const quand = quandSeance({
    debut: new Date(debut),
    fin: new Date(fin),
    maintenant: new Date(instant),
    jour,
    surtitre,
  })
  return <SurtitreEtJour surtitre={quand.surtitre} jour={quand.grand} />
}
